import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Tooltip } from '@creditornot/cb-popover';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography } from 'common/typography';

import { MetricsBlock } from './MetricsBlock';
import { MultiLineTooltip } from '../MultiLineTooltip';

type CampaignAnalyticsMetricsProps = {
  currency: Currency;
  colored?: boolean;
  isLoading?: boolean;
  isSingleCampaign: boolean;
  metrics: {
    totalSales: number;
    totalSpend: number;
    totalOrders: number;
    newCustomers?: number;
    returningCustomers?: number;
    averageBasketValue: number;
    averageBasketItemsCount: number;
    averageCostPerOrder: number;
    totalMerchantSpend?: number;
    totalWoltSpend?: number;
  };
};

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  fill: ${cbSemanticCssVars.textTertiary};
  margin-block-end: -1px;
`;

const TooltipPopover = ({ tooltip }: { tooltip: string }) => (
  <Tooltip content={tooltip}>
    <InfoIcon />
  </Tooltip>
);

export const CampaignAnalyticsMetrics = ({
  colored,
  currency,
  isLoading,
  isSingleCampaign,
  metrics: {
    averageBasketValue,
    averageBasketItemsCount,
    averageCostPerOrder,
    newCustomers,
    returningCustomers,
    totalOrders,
    totalSales,
    totalSpend,
    totalMerchantSpend,
    totalWoltSpend,
  },
}: CampaignAnalyticsMetricsProps) => {
  const { formatMessage } = useIntl();

  return (
    <MetricsBlock
      colored={colored}
      isLoading={isLoading}
      primaryMetrics={[
        {
          title: formatMessage({ id: 'campaigns.analytics.total-sales' }),
          value: <MonetaryAmount amount={totalSales} currency={currency} />,
          tooltip: (
            <TooltipPopover
              tooltip={
                isSingleCampaign
                  ? formatMessage({ id: 'campaigns.analytics.total-sales-tooltip-multiple' })
                  : formatMessage({ id: 'campaigns.analytics.total-sales-tooltip-single' })
              }
            />
          ),
        },
        {
          title: formatMessage({ id: 'campaigns.analytics.total-spend' }),
          value: <MonetaryAmount amount={totalSpend} currency={currency} />,
          tooltip: isSingleCampaign ? null : (
            <MultiLineTooltip
              title={
                <Typography.Small>
                  <FormattedMessage id="campaigns.analytics.total-spend-tooltip-title" />
                </Typography.Small>
              }
              icon={<InfoIcon />}
              tooltipLines={[
                {
                  titleKey: 'campaigns.analytics.total-spend-paid-by-merchant',
                  value: (
                    <Typography.Small>
                      <MonetaryAmount amount={totalMerchantSpend ?? 0} currency={currency} />
                    </Typography.Small>
                  ),
                },
                {
                  titleKey: 'campaigns.analytics.total-spend-paid-by-wolt',
                  value: (
                    <Typography.Small>
                      <MonetaryAmount amount={totalWoltSpend ?? 0} currency={currency} />
                    </Typography.Small>
                  ),
                },
              ]}
            />
          ),
        },
        {
          title: formatMessage({ id: 'campaigns.analytics.total-orders' }),
          value: totalOrders,
          tooltip: (
            <TooltipPopover
              tooltip={
                isSingleCampaign
                  ? formatMessage({ id: 'campaigns.analytics.total-orders-tooltip-multiple' })
                  : formatMessage({ id: 'campaigns.analytics.total-orders-tooltip-single' })
              }
            />
          ),
        },
      ]}
      secondaryMetrics={[
        ...(newCustomers !== undefined
          ? [{ title: formatMessage({ id: 'insights.customers.new-customers' }), value: newCustomers }]
          : []),
        ...(returningCustomers !== undefined
          ? [{ title: formatMessage({ id: 'campaigns.analytics.returning-customers' }), value: returningCustomers }]
          : []),
        {
          title: formatMessage({ id: 'insights.performance.average-basket-size' }),
          value: <MonetaryAmount amount={averageBasketValue} currency={currency} />,
        },
        {
          title: formatMessage({ id: 'insights.performance.average-basket-size-items' }),
          value: averageBasketItemsCount,
        },
        {
          title: formatMessage({ id: 'campaigns.analytics.average-cost-per-order' }),
          value: <MonetaryAmount amount={averageCostPerOrder} currency={currency} />,
          tooltip: (
            <TooltipPopover tooltip={formatMessage({ id: 'campaigns.analytics.average-cost-per-order-tooltip' })} />
          ),
        },
      ]}
    />
  );
};
